import { Injectable } from '@angular/core';
import { ProductService, RmsContractProductsRequest, RmsContractProductsResponse } from '@cemex-core/cmx-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import {
  getActiveExperienceCode,
  getOrderBuilderState,
  OrderBuilderFeatureState,
  OrderBuilderState,
} from '../../../reducers';
import {
  ProductsActionTypes,
  ProductsLoadAction,
  ProductsLoadCompleteAction,
  ProductsResetAction,
} from '../actions/products.actions';
import { RmsSpecificationActionTypes } from '../actions/rms-specification.actions';
import { createProductsRequestPayload } from '@ordersnproducts/modules/order-builder/modules/rms-specification/effects/utils';
import {
  SpecificationLoadCatalogsAction,
  SpecificationLoadProjectProfilesAction,
} from '@ordersnproducts/modules/order-builder/modules/specification/actions/specification.actions';
import { REQUEST_TYPES } from '@ordersnproducts/utils/request-types';

@Injectable()
export class RmsColombiaProductsEffects {
  loadContractProduct$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RmsSpecificationActionTypes.INIT),
      switchMap(() => {
        return this.store.select(getActiveExperienceCode).pipe(
          take(1),
          filter((expCode) => {
            return expCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK;
          }),
          map(() => new ProductsLoadAction()),
        );
      }),
    ),
  );

  loadCatalogs$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RmsSpecificationActionTypes.INIT),
      switchMap(() => {
        return this.store.select(getActiveExperienceCode).pipe(
          take(1),
          filter((expCode) => {
            return expCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK;
          }),
          map(() => new SpecificationLoadCatalogsAction()),
        );
      }),
    ),
  );

  loadProjectProfiles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RmsSpecificationActionTypes.INIT),
      switchMap(() => {
        return this.store.select(getActiveExperienceCode).pipe(
          take(1),
          filter((expCode) => {
            return expCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK;
          }),
          map(() => new SpecificationLoadProjectProfilesAction()),
        );
      }),
    ),
  );

  getProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActionTypes.LOAD),
      switchMap((action: ProductsLoadAction) => {
        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          filter((state) => !!state),
          filter((state) => !!_safe(() => state.locationPage.calendars.calendars[0].subGroup.subGroupCode)),
          switchMap((state) => {
            // loading contract products first
            return this.getContractProducts(state).pipe(
              switchMap((response) => {
                if (!response || response.productRecommendationCom.length) {
                  return of(response);
                } else {
                  // if contract products are empty loading TopProducts
                  return this.getTopRecommendedProducts(state);
                }
              }),
            );
          }),
          map((products) => {
            if (products) {
              return new ProductsLoadCompleteAction(products);
            } else {
              return new ProductsResetAction();
            }
          }),
        );
      }),
    ),
  );

  private getContractProducts(state: OrderBuilderFeatureState): Observable<RmsContractProductsResponse | null> {
    const payload = createProductsRequestPayload(state);
    return this.getProducts(payload);
  }

  private getTopRecommendedProducts(state: OrderBuilderFeatureState): Observable<RmsContractProductsResponse | null> {
    const payload = createProductsRequestPayload(state, REQUEST_TYPES.TopNRequest);
    return this.getProducts(payload);
  }

  private getProducts(payload: RmsContractProductsRequest): Observable<RmsContractProductsResponse | null> {
    return this.productService.getContractProducts(payload, { NO_RETRY: 'NO_RETRY' }).pipe(
      catchError((err) => {
        return of(null);
      }),
    );
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private productService: ProductService,
  ) {}
}
